import {
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  Card,
  CrudMode,
  Dialog,
  handleClose,
  Page,
  PageRoute,
  ScreenType,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { Permission, StudentParent, StudentParentEditResource, StudentParentViewResource } from "generated/openApi";
import "moment-timezone";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { StudentParentPageBody } from "./StudentParentPageBody";
import { StudentParentPageHeader } from "./StudentParentPageHeader";

const routeName = "studentParent";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
  screenType: ScreenType = "standard";
}

class OptionalParams {
  studentId?: number = 0;
}

export const studentParentPageRoute: PageRoute<Params, OptionalParams> = {
  handle: "Parent",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
  getOptionalParams: (params) => buildOptionalParams(params),
};

export const StudentParentPage = () => {
  const ROOT_PERMISSION: Permission = Permission.StudentParent;
  const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
  const navigate = useNavigate();
  const [studentParent, setStudentParent] = useState<StudentParent>({} as StudentParent);
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [studentParent] });
  const [viewResource, setViewResource] = useState<StudentParentViewResource>({});
  const [editResource, setEditResource] = useState<StudentParentEditResource>({});
  const { validationErrors, setValidationErrors } = useValidationErrors();

  useEffect(() => {
    fetch(params.id);
    fetchViewResource();
  }, []);

  useEffect(() => {
    if (params.crudMode === "edit") fetchEditResource();
  }, [params.crudMode]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(`../${studentParentPageRoute.getPath({ id: id, crudMode: crudMode, screenType: params.screenType })}`);

  const fetch = (id: number) => {
    ApiService.studentParents.get(id).then((response) => {
      if (id === 0) {
        if (optionalParams.studentId) response.studentId = optionalParams.studentId;
      }

      setStudentParent(response);
      resetUnsavedChanges([response]);
    });
  };

  const fetchViewResource = () => {
    ApiService.studentParents.getViewResource().then((response) => {
      setViewResource(response);
    });
  };

  const fetchEditResource = () => {
    ApiService.studentParents.getEditResource().then((response) => {
      setEditResource(response);
    });
  };

  const pageHeader = (
    <StudentParentPageHeader
      rootPermission={ROOT_PERMISSION}
      handle={studentParentPageRoute.handle}
      crudMode={params.crudMode}
      reroute={reroute}
      fetch={fetch}
      studentParent={studentParent}
      setValidationErrors={setValidationErrors}
      unsavedChanges={unsavedChanges}
    />
  );

  const pageBody = (
    <StudentParentPageBody
      crudMode={params.crudMode}
      studentParent={studentParent}
      setStudentParent={setStudentParent}
      viewResource={viewResource}
      editResource={editResource}
      validationErrors={validationErrors}
    />
  );

  return params.screenType === "standard" ? (
    <Page isForm header={pageHeader}>
      <Card>{pageBody}</Card>
    </Page>
  ) : (
    <Dialog
      isForm
      open={true}
      onClose={() => handleClose(navigate, unsavedChanges)}
      maxWidth="md"
      header={pageHeader}
      content={pageBody}
    />
  );
};
